import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import logo from "./images/logo.png";
import Venue from "./pages/Venue/Venue";
import Menu from "./pages/Menu/Menu";
import Product from "./pages/Product/Product";
import Order from "./pages/Order/Order";
import Payment from "./pages/Payment/Payment";
import Confirmation from "./pages/Confirmation/Confirmation";
import Summary from "./pages/Summary/Summary";
import { HashRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import VenueProvider from "./stores/VenueProvider";
import OrderProvider from "./stores/OrderProvider";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import * as API from "./api/api.js";
import smoothscroll from "smoothscroll-polyfill";
import { t } from "./i18n";

import { styleSelector } from "./components/styles/styleSelector";
import { fetchCustomStylesheet } from "./components/styles/fetchCustomStylesheet";

const getVenueIdFromHash = () => window.location.hash.slice(1).split("/")[0];

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc: "https://fonts.googleapis.com/css?family=Muli&display=swap",
    },
  ],
};

smoothscroll.polyfill();

export default () => {
  const [venueId, setVenueId] = useState(getVenueIdFromHash);

  // Remount the tree when the venue id in the hash changes (paste / QR scan).
  // Without this, HashRouter keeps the old basename and no route matches → white screen.
  useEffect(() => {
    const onHashChange = () => {
      const nextVenueId = getVenueIdFromHash();
      setVenueId((current) => (current === nextVenueId ? current : nextVenueId));
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const stripe = useMemo(() => {
    if (!API.PAYMENT_ENABLED) {
      return Promise.resolve(null);
    }
    if (!venueId) {
      return Promise.resolve(null);
    }
    return API.getStripeKey(venueId)
      .then((result) => loadStripe(result.key))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[stripe] failed to load publishable key', error);
        return null;
      });
  }, [venueId]);

  styleSelector();

  useEffect(() => {
    fetchCustomStylesheet(venueId);
  }, [venueId]);

  return (
    <div className='main-container cst-main-container cst-global-main-container' key={venueId}>
      <div className='logo-container'>
        <a href='http://appero.co'>
          <img src={logo} alt={t('a11y.logo')} />
        </a>
      </div>
      <VenueProvider>
        <OrderProvider>
          <Elements stripe={stripe} options={ELEMENTS_OPTIONS}>
            <Router basename={venueId} hashType='noslash'>
              <Switch>
                <Route exact path='/' component={Venue} />
                <Route exact path='/table/:table' component={Venue} />
                <Route path='/menu' component={Menu} />
                <Route path='/product/:id' render={(routeProps) => <Product {...routeProps} key={document.location.href} />} />
                <Route path='/order' component={Order} />
                <Route path='/payment' render={() => (
                  API.PAYMENT_ENABLED ? <Payment /> : <Redirect to='/order' />
                )} />
                <Route path='/confirmation' component={Confirmation} />
                <Route path='/summary' component={Summary} />
              </Switch>
            </Router>
          </Elements>
        </OrderProvider>
      </VenueProvider>
    </div>
  );
};
